import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * WishlistContext — signed-in clients keep a set of item ids they love.
 * Storage is server-side (per user) so it follows them across devices.
 * Guests get a no-op interface so callers don't need to guard.
 */
const WishlistContext = createContext({
    ids: new Set(),
    isWishlisted: () => false,
    toggle: () => {},
    count: 0,
    ready: false,
});

export const WishlistProvider = ({ children }) => {
    const { user } = useAuth();
    const [ids, setIds] = useState(new Set());
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!user) { setIds(new Set()); setReady(false); return; }
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/wishlist/ids");
                if (alive) setIds(new Set(data.ids || []));
            } catch { /* ignore — guest or transient */ }
            finally { if (alive) setReady(true); }
        })();
        return () => { alive = false; };
    }, [user]);

    const toggle = useCallback(async (itemId) => {
        if (!user) return { requiresAuth: true };
        const wasIn = ids.has(itemId);
        // Optimistic update
        setIds((s) => {
            const next = new Set(s);
            if (wasIn) next.delete(itemId); else next.add(itemId);
            return next;
        });
        try {
            if (wasIn) await api.delete(`/wishlist/${itemId}`);
            else await api.post("/wishlist", { item_id: itemId });
            return { added: !wasIn };
        } catch (e) {
            // rollback
            setIds((s) => {
                const next = new Set(s);
                if (wasIn) next.add(itemId); else next.delete(itemId);
                return next;
            });
            throw e;
        }
    }, [user, ids]);

    const isWishlisted = useCallback((id) => ids.has(id), [ids]);

    return (
        <WishlistContext.Provider value={{ ids, isWishlisted, toggle, count: ids.size, ready }}>
            {children}
        </WishlistContext.Provider>
    );
};

export const useWishlist = () => useContext(WishlistContext);
